import { apiClient } from '../apiClient';

/**
 * A question about the student's own solution.
 *
 * Output prediction, not "explain your solution". Explaining is the question a
 * chat assistant answers well on your behalf; predicting what a particular
 * program prints for a particular input means engaging with the actual code.
 *
 * The expected output is deliberately not part of this shape — it is the
 * answer, and it never leaves the server.
 */
export interface Challenge {
  problem_slug: string;
  problem_title: string;
  /** The input their code was run against. */
  input: string;
  /** Their own submitted code, so the question is answerable in place. */
  code: string;
  language?: string;
  /** Already answered: show the outcome rather than asking again. */
  answered: boolean;
  correct: boolean;
}

export interface Verdict {
  correct: boolean;
  /**
   * What the code actually prints. Returned whether or not the answer was
   * right — a student who mispredicted their own program should find out what
   * it does while they still care.
   */
  expected_output: string;
}

export const comprehensionApi = {
  /**
   * Returns null when there is no question to ask, which is the normal case:
   * most problems keep their test cases in object storage rather than inline,
   * and the student should see nothing at all rather than an apology.
   */
  getChallenge: async (slug: string): Promise<Challenge | null> => {
    const res = await apiClient.get<Challenge | ''>(`/api/v1/comprehension/problems/${slug}`);
    // 204 No Content arrives as an empty body.
    return res.status === 204 || !res.data ? null : (res.data as Challenge);
  },
  answer: async (slug: string, answer: string) => {
    const { data } = await apiClient.post<Verdict>(
      `/api/v1/comprehension/problems/${slug}/answer`,
      { answer },
    );
    return data;
  },
};

/**
 * One model-chosen input, and the student's prediction of what their code does
 * with it.
 *
 * `expected` is absent by design — it never leaves the server.
 */
export type QuestionKind = 'output' | 'choice';

export interface ComprehensionQuestion {
  position: number;
  kind: QuestionKind;
  /** output kind: the input their code was run against. */
  input?: string;
  /** choice kind: the question and its options. The answer is not sent. */
  prompt?: string;
  options?: string[];
  /** The model's stated reason for the question, under a dozen words. */
  rationale?: string;
  answer?: string;
  answered: boolean;
  correct: boolean;
}

export interface QuestionSet {
  problem_slug: string;
  problem_title: string;
  /** Their own submitted code, so the questions are answerable in place. */
  code: string;
  language?: string;
  questions: ComprehensionQuestion[];
}

/**
 * Either a prepared set, or word that one is being prepared, or nothing.
 *
 * Three states rather than two: choosing inputs takes about half a minute
 * against a reasoning model, so "ask again shortly" has to be distinguishable
 * from "there is nothing here for this problem".
 */
export type QuestionSetResult =
  | { status: 'ready'; set: QuestionSet }
  | { status: 'preparing' }
  | { status: 'none' };

export const questionSetApi = {
  get: async (slug: string): Promise<QuestionSetResult> => {
    const res = await apiClient.get<QuestionSet | { preparing: boolean } | ''>(
      `/api/v1/comprehension/problems/${slug}/questions`,
    );
    // Status before body: a 202 carries {"preparing":true}, which is truthy, so
    // the old `!res.data` ordering happened to work — but it made "no content"
    // depend on the shape of a body rather than on the status the server sent.
    if (res.status === 202) return { status: 'preparing' };
    if (res.status === 204 || !res.data) return { status: 'none' };
    return { status: 'ready', set: res.data as QuestionSet };
  },
  answer: async (slug: string, position: number, answer: string) => {
    const { data } = await apiClient.post<Verdict>(
      `/api/v1/comprehension/problems/${slug}/questions/${position}/answer`,
      { answer },
    );
    return data;
  },
};
